import { autorun, makeAutoObservable, runInAction } from 'mobx';
import ModalDelegate from 'store/general/modalDelegate';
import SubscriberDependentDelegate from 'store/modules/elections/dependents/subDependentDelegate';
import { useEffect, useState } from 'react';
import SubscriberDelegate from '../subscriberDelegate';

interface DomainData {
  dependentDelegate: SubscriberDependentDelegate;
  subscriberDelegate: SubscriberDelegate;
}

export class RemoveDependentModalStore {
  private readonly _dependentDelegate: SubscriberDependentDelegate;
  private readonly _subscriberDelegate: SubscriberDelegate;
  private _removeDependentModal = new ModalDelegate();
  private _isLoading = false;

  public get showRemoveDependentModal(): boolean {
    return this._removeDependentModal.isVisible;
  }

  public get isLoading(): boolean {
    return this._isLoading;
  }

  private _setLoading(loadingStatus: boolean): void {
    this._isLoading = loadingStatus;
  }

  public openRemoveDependentModal(): void {
    this._removeDependentModal.open();
  }

  public closeRemoveDependentModal(): void {
    this._removeDependentModal.close();
  }

  public async removeDependent(): Promise<void> {
    this._setLoading(true);
    await this._subscriberDelegate.removeDependent(this._dependentDelegate.dependent.id);
    runInAction(() => {
      this._setLoading(false);
      this.closeRemoveDependentModal();
    });
  }

  constructor({ dependentDelegate, subscriberDelegate }: DomainData) {
    this._dependentDelegate = dependentDelegate;
    this._subscriberDelegate = subscriberDelegate;

    makeAutoObservable(this, {}, { autoBind: true });
  }
}

export const useRemoveDependentModalStore = ({
  dependentDelegate,
  subscriberDelegate,
}: DomainData): RemoveDependentModalStore | null => {
  const [store, setStore] = useState<RemoveDependentModalStore | null>(null);

  useEffect(() => {
    autorun(() => {
      setStore(new RemoveDependentModalStore({ dependentDelegate, subscriberDelegate }));
    });

    return (): void => {
      setStore(null);
    };
  }, [dependentDelegate, subscriberDelegate]);

  return store;
};
